import useFlowNextStore from '$/store/useTeamFlowStore';
import type {
  FlowNodeData,
  ModelConfig,
  ModelToolParameterItem,
  ModelToolParameterType,
} from '$/domains/workFlowV3/type';
import {
  FlowNodeBaseType,
  WorkflowNodeLinesData,
  WorkflowNodeEntity,
  WorkflowSelectService,
  type FlowNodeEntity,
  type FreeLayoutPluginContext,
} from '@flowgram.ai/free-layout-editor';
import { mtstatTrack } from '$/utils/mtstat';
import { NODE_TYPE_TO_MODEL_TYPE_MAP, type ModelNodeType } from '../config/node.config';
import {
  excludeLockedScriptStoryboardChildren,
  isLockedScriptStoryboardGroup,
} from './lockedScriptStoryboard';
import {
  isNodeTaskExecuting,
  isUnderExecutingGroupConstraint,
} from './nodeExecutionState';

export * from './applyLeftLinkedNodes';
export * from './isNodeInStoryboard';
export * from './lockedScriptStoryboard';
export {
  isExecutingGroupNode,
  isGroupTaskExecuting,
  isNodeDeletionBlockedWhileExecuting,
  isNodeInsideExecutingGroup,
  isNodeTaskExecuting,
  isUnderExecutingGroupConstraint,
} from './nodeExecutionState';

type FlowNextPromptState = {
  modelConfig?: ModelConfig;
  roomId?: string;
};

/**
 * 读取 TeamFlow store，局部隔离当前 store 类型在全量 tsc 下被推成 never 的问题。
 */
function getFlowNextPromptState(): FlowNextPromptState {
  const store = useFlowNextStore as unknown as { getState: () => FlowNextPromptState; };
  return store.getState();
}

/**
   * 处理节点列表，根据 isIncludeGroupNodeSelf 参数决定是否包含组节点本身
   * @param nodes 要处理的节点数组
   * @param isIncludeGroupNodeSelf true: 返回所有节点 + 组内节点；false: 排除组节点自身
   * @returns 处理后的节点列表
   */
export const getProcessedSelectedNodes = (
  nodes: FlowNodeEntity[],
  isIncludeGroupNodeSelf: boolean,
): FlowNodeEntity[] => {
  if (nodes.length === 0) return [];

  const resultNodes: FlowNodeEntity[] = [];
  const nodeIdSet = new Set<string>();
  nodes.forEach((node) => {
    const nodeData = node.toJSON();
    const isGroupNode = nodeData.type === FlowNodeBaseType.GROUP;
    if (isGroupNode) {
      // 如果是群组节点，添加群组内的所有节点
      const blocks = node.blocks || [];
      blocks.forEach((block: FlowNodeEntity) => {
        if (!nodeIdSet.has(block.id)) {
          nodeIdSet.add(block.id);
          resultNodes.push(block);
        }
      });
      if (isIncludeGroupNodeSelf) {
        if (!nodeIdSet.has(node.id)) {
          nodeIdSet.add(node.id);
          resultNodes.push(node);
        }
      }
      return;
    }
    // 普通节点直接添加
    if (!nodeIdSet.has(node.id)) {
      nodeIdSet.add(node.id);
      resultNodes.push(node);
    }
  });
  return resultNodes;
};

export interface MergeGroupResolution {
  groupNodesToUngroup: FlowNodeEntity[];
  nodesToGroup: FlowNodeEntity[];
}

/**
 * 获取节点所在的群组父节点。
 */
export function getParentGroupNode(node: FlowNodeEntity): FlowNodeEntity | undefined {
  const { parent } = node;
  if (!parent || parent.flowNodeType !== FlowNodeBaseType.GROUP) {
    return undefined;
  }
  return parent;
}

/**
 * 从节点列表中排除不可打组/复制的节点：执行中的普通节点、执行中群组及其内部节点。
 */
export function excludeExecutingGroups(nodes: FlowNodeEntity[]): FlowNodeEntity[] {
  return nodes.filter((node) => {
    if (isUnderExecutingGroupConstraint(node)) {
      return false;
    }
    if (isNodeTaskExecuting(node)) {
      return false;
    }
    return true;
  });
}

/**
 * 解析框选多参连线的选区节点：生成中节点可作为连线源，仅排除执行中群组约束。
 */
export function getMultiConnectSelectionNodes(nodes: FlowNodeEntity[]): FlowNodeEntity[] {
  return nodes.filter((node) => !isUnderExecutingGroupConstraint(node));
}

/**
 * 从选区中移除指定节点及其父群组，任务开始执行时立即取消焦点，避免 pending 生效前仍可拖拽。
 */
export function clearSelectionForNodesAndParentGroups(
  ctx: FreeLayoutPluginContext,
  nodes: FlowNodeEntity[],
): void {
  const selectService = ctx.get(WorkflowSelectService);
  const idsToClear = new Set<string>();
  nodes.forEach((node) => {
    idsToClear.add(node.id);
    const parentGroup = getParentGroupNode(node);
    if (parentGroup) {
      idsToClear.add(parentGroup.id);
    }
  });
  const filtered = selectService.selection.filter((entity) => {
    if (!(entity instanceof WorkflowNodeEntity)) {
      return true;
    }
    return !idsToClear.has(entity.id);
  });
  if (filtered.length !== selectService.selection.length) {
    selectService.selection = filtered;
  }
}

/**
 * 解析多选打组场景：排除执行中群组，展开其余群组内子节点，并收集需要先解组的群组节点。
 */
export function resolveNodesForMergeGroup(
  selectedNodes: FlowNodeEntity[],
): MergeGroupResolution {
  const eligibleSelection = excludeLockedScriptStoryboardChildren(excludeExecutingGroups(selectedNodes));
  const groupNodesToUngroup = eligibleSelection.filter(
    (node) => node.toJSON().type === FlowNodeBaseType.GROUP
      && !isLockedScriptStoryboardGroup(node),
  );
  const nodesToGroup = getProcessedSelectedNodes(eligibleSelection, false)
    .filter((node) => !isNodeTaskExecuting(node));

  return {
    groupNodesToUngroup,
    nodesToGroup,
  };
}

/**
 * 从当前 render data 优先解析 prompt key，避免新节点刚创建时 node.toJSON 尚未同步导致输入框为空。
 */
export function getMcpPromptKeyFromNodeData(
  node: FlowNodeEntity,
  nodeData?: FlowNodeData,
  toolParameters?: Record<string, ModelToolParameterItem>,
): string | undefined {
  const promptKey = getMcpInfoParameterKey(node, 'prompt');
  if (promptKey) return promptKey;

  const toolPromptKey = Object.keys(toolParameters ?? {}).find(
    (key) => toolParameters?.[key]?.type === 'prompt',
  );
  if (toolPromptKey) return toolPromptKey;

  return typeof nodeData?.mcpInfo?.parameters?.prompt === 'string' ? 'prompt' : undefined;
}

/**
 * 从当前 render data 优先读取 prompt 值，保留 node.toJSON 与默认参数作为兜底。
 */
export function getMcpPromptValueFromNodeData(
  node: FlowNodeEntity,
  nodeData?: FlowNodeData,
  toolParameters?: Record<string, ModelToolParameterItem>,
  fallbackParameters?: Record<string, unknown>,
): string | undefined {
  const promptKey = getMcpPromptKeyFromNodeData(node, nodeData, toolParameters);
  if (!promptKey) return undefined;

  const dataPrompt = nodeData?.mcpInfo?.parameters?.[promptKey];
  if (typeof dataPrompt === 'string') return dataPrompt;

  const nodeJsonData = node.toJSON()?.data as FlowNodeData | undefined;
  const nodePrompt = nodeJsonData?.mcpInfo?.parameters?.[promptKey];
  if (typeof nodePrompt === 'string') return nodePrompt;

  const fallbackPrompt = fallbackParameters?.[promptKey];
  return typeof fallbackPrompt === 'string' ? fallbackPrompt : undefined;
}

export type NodeSource =
  | 'edit_add_click'
  | 'edit_mynode_collect_click'
  | 'edit_mynode_preset_click'
  | 'edit_asset_click'
  | 'edit_material_click'
  | 'recommend_click'
  | 'fast_start_click'
  | 'external'
  | 'ctrl_c'
  | 'copy'
  | 'create_copy'
  | 'generate'
  | 'func_guide'
  | 'agent_teams_chat'
  | 'upload'
  | 'official_card'
  | 'template_click'
  | 'downstream_tool'
  | 'video_clip';

export type ContentSource =
  | 'agent_teams'
  | 'upload'
  | 'official_card'
  | 'workflow_run'
  | 'sed_editing'
  | 'no_content';

/**
 * 从媒体对象中按候选字段提取埋点值，兼容后端与前端历史字段命名。
 */
function collectNodeCreateMediaValues(
  mediaList: FlowNodeData['media_list'] | undefined,
  keys: string[],
): string {
  if (!Array.isArray(mediaList)) return '';
  return mediaList
    .map((item) => {
      const media = item as Record<string, unknown>;
      return keys.map((key) => media[key]).find(Boolean);
    })
    .filter(Boolean)
    .map(String)
    .join(',');
}

/**
 * 收集创建节点的上游节点 ID，后链路节点优先从真实连线兜底。
 */
function collectNodeCreateBeforeNodeIds(
  node: FlowNodeEntity,
  data: FlowNodeData | undefined,
): string {
  const beforeNodeIds: string[] = [];
  if (data?.inputNodeId) {
    const {
      textNodeIds, imageNodeIds, videoNodeIds, audioNodeIds,
    } = data.inputNodeId;
    beforeNodeIds.push(
      ...(textNodeIds || []),
      ...(imageNodeIds || []),
      ...(videoNodeIds || []),
      ...(audioNodeIds || []),
    );
  }
  if (!beforeNodeIds.length) {
    const inputNodes = node.getData(WorkflowNodeLinesData)?.inputNodes ?? [];
    beforeNodeIds.push(...inputNodes.map((inputNode) => inputNode.id));
  }
  return beforeNodeIds.join(',');
}

/**
 * 根据节点 mcpInfo 回查模型名称，避免后链路节点没有直接写入 model_name 时上报空值。
 */
function resolveNodeCreateModelName(
  node: FlowNodeEntity,
  data: FlowNodeData | undefined,
): string {
  const mcpInfo = (data as Record<string, any> | undefined)?.mcpInfo;
  if (mcpInfo?.model_name) return mcpInfo.model_name;

  const modelType = NODE_TYPE_TO_MODEL_TYPE_MAP[node.flowNodeType as ModelNodeType];
  const models = modelType ? getFlowNextPromptState().modelConfig?.[modelType] ?? [] : [];
  const matchedModel = models.find((model) => model.model_id === mcpInfo?.model_id)
    ?? models.find((model) => model.tool_list?.some((tool) => {
      if (mcpInfo?.node_model_id && tool.node_model_id === mcpInfo.node_model_id) return true;
      if (mcpInfo?.api_name && tool.api_name === mcpInfo.api_name) return true;
      return false;
    }));
  return matchedModel?.model_name ?? '';
}

/**
 * 创建节点埋点（roboneo_node_create），完成一个节点上报一次。
 *
 * @export
 * @param {FlowNodeEntity} node - 被创建的节点
 * @param {object} params - 调用方提供的上下文参数
 * @param {NodeSource} params.node_source - 节点来源
 * @param {ContentSource} params.content_source - 节点内容来源
 * @param {string} [params.fast_start_name] - 快速开始入口名称，仅快速开始创建节点时透传
 * @param {FlowNodeData} [params.data] - 可选节点数据快照，用于异步写回场景精准上报
 */
export function roboneoNodeCreate(
  node: FlowNodeEntity,
  params: {
    node_source: NodeSource;
    content_source: ContentSource;
    fast_start_name?: string;
    data?: FlowNodeData;
  },
) {
  const room_id = getFlowNextPromptState().roomId;
  const data = params.data ?? (node.toJSON?.()?.data as FlowNodeData | undefined);
  const mcpInfo = (data as Record<string, any>)?.mcpInfo;
  const beforeNodeId = collectNodeCreateBeforeNodeIds(node, data);

  mtstatTrack('roboneo_node_create', {
    room_id,
    node_id: node.id,
    content_type: node.flowNodeType,
    node_source: params.node_source,
    content_source: params.content_source,
    content_urls: collectNodeCreateMediaValues(data?.media_list, ['url', 'originUrl', 'uri']),
    task_id: collectNodeCreateMediaValues(data?.media_list, ['task_id']),
    sub_task_id: collectNodeCreateMediaValues(data?.media_list, ['sub_task_id']),
    material_id: collectNodeCreateMediaValues(data?.media_list, ['material_id']),
    api_name: mcpInfo?.api_name || '',
    before_node_id: beforeNodeId,
    model_name: resolveNodeCreateModelName(node, data),
    fast_start_name: params.fast_start_name || '',
  });
}

/**
 * 获取节点 mcpInfo 中指定类型的参数 key
 *
 * @export
 * @param {FlowNodeEntity} node
 * @param {(Omit<ModelToolParameterType, 'select' | 'slider' | 'switch'>)} type
 * @return {*}
 */
export function getMcpInfoParameterKey(node: FlowNodeEntity, type: Omit<ModelToolParameterType, 'select' | 'slider' | 'switch'>) {
  const data = node.toJSON()?.data as FlowNodeData;
  const { modelConfig } = useFlowNextStore.getState();
  const modelType = NODE_TYPE_TO_MODEL_TYPE_MAP[node.flowNodeType as ModelNodeType];
  if (!modelType) return undefined;

  const typeModelConfigs = modelConfig?.[modelType] ?? [];
  if (!typeModelConfigs) return undefined;

  const matchedModel = typeModelConfigs.find((model) => model.model_id === data?.mcpInfo?.model_id);
  if (!matchedModel) return undefined;

  const matchedTool = matchedModel.tool_list?.find((tool) => tool.api_name === data?.mcpInfo?.api_name);
  if (!matchedTool) return undefined;

  const parameterKey = Object.keys(matchedTool.parameters).find((key) => matchedTool.parameters[key]?.type === type);
  return parameterKey;
}

/**
 * 获取节点 mcpInfo 中指定类型的参数值
 *
 * @export
 * @param {FlowNodeEntity} node
 * @param {(Omit<ModelToolParameterType, 'select' | 'slider' | 'switch'>)} type
 * @return {*}
 */
export function getMcpInfoParameterValue(node: FlowNodeEntity, type: Omit<ModelToolParameterType, 'select' | 'slider' | 'switch'>) {
  const key = getMcpInfoParameterKey(node, type);
  if (!key) return undefined;

  const { mcpInfo } = (node.toJSON()?.data ?? {}) as FlowNodeData;
  return mcpInfo?.parameters?.[key];
}
